// Tandem section — beta testers only, Revolt Desktop only (local filesystem workflows)
// Layout mirrors other desktop surfaces: shared site Header (MainLayout) + DesktopHeader + content

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MainLayout } from "../../components/Layout";
import { LoadingPanel } from "../../components";
import { useManager, logger } from "../../core";
import { useAuthRequired } from "../../contexts/AuthContext";
import { DesktopHeader } from "../desktop/components";
import { TandemManager } from "./managers/tandem-manager";
import { TandemWorkingDirectoryStep, TandemChatInterface } from "./components";
import "./TandemSection.css";

function isDesktopEnvironment() {
  return typeof window !== "undefined" && !!window.navigationAPI;
}

/** Full-page content phases (no tab chrome — one full view at a time). */
const PHASE_WORKING_DIRECTORY = "working-directory";
const PHASE_CHAT = "chat";

export function TandemSection() {
  const location = useLocation();

  const auth = useAuthRequired({
    requireAuth: true,
    requireBetaTester: true,
    payload: {
      action: "viewTandem",
      section: "tandem",
      timestamp: Date.now(),
    },
  });

  const { manager: tandemManager, isInitialized } = useManager(TandemManager);
  const tandemChatInterfaceRef = useRef(null);

  const [flowPhase, setFlowPhase] = useState(PHASE_WORKING_DIRECTORY);
  const [directoryPath, setDirectoryPath] = useState("");
  const [workingProjectName, setWorkingProjectName] = useState("");
  const [workingProjectId, setWorkingProjectId] = useState("");

  const resetFlowToStart = useCallback(() => {
    setFlowPhase(PHASE_WORKING_DIRECTORY);
    setDirectoryPath("");
    setWorkingProjectName("");
    setWorkingProjectId("");
  }, []);

  useEffect(() => {
    if (!auth.loading && auth.isAuthorized && !isDesktopEnvironment()) {
      logger.warn(
        { tags: "section|tandem|access", color1: "yellow" },
        "Tandem opened outside Revolt Desktop (navigationAPI missing)"
      );
    }
  }, [auth.loading, auth.isAuthorized]);

  // Keep Tool-system file tools aligned with the folder the user typed or picked.
  useEffect(() => {
    if (tandemManager && typeof tandemManager.setWorkingDirectoryPath === "function") {
      tandemManager.setWorkingDirectoryPath(directoryPath);
    }
  }, [tandemManager, directoryPath]);

  useEffect(() => {
    if (
      flowPhase === PHASE_WORKING_DIRECTORY &&
      tandemManager &&
      typeof tandemManager.clearTandemProjectContext === "function"
    ) {
      tandemManager.clearTandemProjectContext();
    }
  }, [flowPhase, tandemManager]);

  // Each visit to this route (after manager init): start at project list; directory/name reset.
  useEffect(() => {
    if (isInitialized) {
      resetFlowToStart();
    }
  }, [location.pathname, isInitialized, resetFlowToStart]);

  if (auth.loading || !auth.isAuthorized) {
    return null;
  }

  if (!isDesktopEnvironment()) {
    return (
      <MainLayout contentClassName="main-content--tandem main-content--tandem-denied">
        <div className="tandem-access-denied">
          <h2>Revolt Desktop only</h2>
          <p>
            Tandem is built for local files on your machine. Open it from the
            Revolt Desktop app (this page is not available in the browser).
          </p>
        </div>
      </MainLayout>
    );
  }

  if (!isInitialized) {
    return (
      <div className="desktop-layout">
        <DesktopHeader activePage="tandem" />
        <MainLayout contentClassName="main-content--tandem">
          <div className="tandem-loading">
            <LoadingPanel
              title="Tandem"
              subtitle="Preparing workspace…"
              showAppName={false}
            />
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div className="desktop-layout">
      <DesktopHeader activePage="tandem" />
      <MainLayout contentClassName="main-content--tandem">
        <div
          className={
            flowPhase === PHASE_CHAT
              ? "tandem-desktop-shell tandem-desktop-shell--chat"
              : "tandem-desktop-shell tandem-desktop-shell--projects"
          }
        >
          {flowPhase === PHASE_WORKING_DIRECTORY && (
            <TandemWorkingDirectoryStep
              directoryPath={directoryPath}
              onDirectoryPathChange={setDirectoryPath}
              onContinue={(meta) => {
                if (meta && typeof meta.name === "string") {
                  setWorkingProjectName(meta.name);
                } else {
                  setWorkingProjectName("");
                }
                if (meta && typeof meta.projectId === "string") {
                  setWorkingProjectId(meta.projectId);
                } else {
                  setWorkingProjectId("");
                }
                setFlowPhase(PHASE_CHAT);
              }}
            />
          )}
          {flowPhase === PHASE_CHAT && (
            <TandemChatInterface
              user={auth.user}
              tandemManager={tandemManager}
              tandemChatInterfaceRef={tandemChatInterfaceRef}
              workingDirectoryPath={directoryPath}
              workingProjectName={workingProjectName}
              workingProjectId={workingProjectId}
              onBackToDirectory={() => setFlowPhase(PHASE_WORKING_DIRECTORY)}
            />
          )}
        </div>
      </MainLayout>
    </div>
  );
}
